import { GLYPH } from '../glyphs';
import { formatDate, formatTime } from './date';
import { toIntlLocale } from './locale';

/**
 * Относительные подписи моментов (ТЗ ACC 4.7.2–4.7.3): сервер отдаёт моменты в ISO 8601
 * (`last_activity_at`, `last_login_at`, `occurred_at`) без готовых подписей — подписи строятся на
 * клиенте через Intl на языке интерфейса, в поясе пользователя, **календарными днями** (вчера в 23:59
 * — это «вчера», а не «сегодня»):
 *  - `relativeMoment` — «Последняя активность», «Последний вход»: «сегодня · 11:20»,
 *    «вчера · 21:04», «4 дня назад» (до 30 дней), дальше — дата («14 марта 2025 г.»);
 *  - `eventMoment` — момент записи журнала: «сегодня · 11:20», «вчера · 21:04», «14 марта · 12:00»,
 *    в другом году — с годом.
 * Подпись отсутствия момента («ещё не входил») — у вызывающего: она зависит от раздела.
 */
export interface MomentOptions {
  /** Текущий момент (для тестов); по умолчанию — сейчас. */
  now?: Date;
  /** IANA-пояс; по умолчанию — пояс браузера пользователя. */
  timeZone?: string;
}

/** День и время: «сегодня» + «11:20». */
export interface DayTime {
  day: string;
  time: string;
}

export type RelativeMoment = ({ kind: 'dayTime' } & DayTime) | { kind: 'text'; text: string };

/** До скольких дней давности момент показывается как «N дней назад». */
export const RELATIVE_DAYS_LIMIT = 30;

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_TIME_SEPARATOR = ` ${GLYPH.middot} `;

function parseMoment(value: string | Date): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

interface CalendarDay {
  year: number;
  /** Номер дня от эпохи — для разницы в календарных днях. */
  ordinal: number;
}

function calendarDay(date: Date, timeZone: string | undefined): CalendarDay {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((item) => item.type === type)?.value ?? Number.NaN);
  const year = part('year');
  return { year, ordinal: Date.UTC(year, part('month') - 1, part('day')) / DAY_MS };
}

function daysBetween(date: Date, options: MomentOptions): { days: number; sameYear: boolean } {
  const now = calendarDay(options.now ?? new Date(), options.timeZone);
  const then = calendarDay(date, options.timeZone);
  // Момент «из будущего» (часы клиента отстают) — это «сегодня».
  return { days: Math.max(0, now.ordinal - then.ordinal), sameYear: now.year === then.year };
}

/** «сегодня» / «вчера» на языке интерфейса (Intl.RelativeTimeFormat, numeric: auto). */
function nearDay(daysAgo: 0 | 1, language: string): string {
  return new Intl.RelativeTimeFormat(toIntlLocale(language), { numeric: 'auto' }).format(
    -daysAgo,
    'day',
  );
}

/** «сегодня · 11:20» — день и время через типографскую точку (GLYPH.middot), как в прототипе. */
export function joinDayTime({ day, time }: DayTime): string {
  return `${day}${DAY_TIME_SEPARATOR}${time}`;
}

/** «Последняя активность» / «Последний вход»: части подписи (для разметки) или null. */
export function relativeMoment(
  value: string | Date,
  language: string,
  options: MomentOptions = {},
): RelativeMoment | null {
  const date = parseMoment(value);
  if (!date) return null;
  const { days } = daysBetween(date, options);
  if (days === 0 || days === 1) {
    return {
      kind: 'dayTime',
      day: nearDay(days, language),
      time: formatTime(date, language, options),
    };
  }
  if (days <= RELATIVE_DAYS_LIMIT) {
    const text = new Intl.RelativeTimeFormat(toIntlLocale(language), {
      numeric: 'always',
    }).format(-days, 'day');
    return { kind: 'text', text };
  }
  return { kind: 'text', text: formatDate(date, language, options) };
}

/**
 * «Последняя активность» / «Последний вход» строкой: «сегодня · 11:20», «4 дня назад»,
 * «14 марта 2025 г.». Нет момента или он некорректен — null (подпись «ещё не входил» — у раздела).
 */
export function formatRelativeMoment(
  value: string | Date | null | undefined,
  language: string,
  options: MomentOptions = {},
): string | null {
  if (value === null || value === undefined || value === '') return null;
  const moment = relativeMoment(value, language, options);
  if (!moment) return null;
  return moment.kind === 'dayTime' ? joinDayTime(moment) : moment.text;
}

/** Момент записи журнала: «сегодня» / «вчера» / «14 марта» / «14 марта 2025 г.» + время. */
export function eventMoment(
  value: string | Date,
  language: string,
  options: MomentOptions = {},
): DayTime | null {
  const date = parseMoment(value);
  if (!date) return null;
  const { days, sameYear } = daysBetween(date, options);
  const time = formatTime(date, language, options);
  if (days === 0 || days === 1) return { day: nearDay(days, language), time };
  if (!sameYear) return { day: formatDate(date, language, options), time };
  const day = new Intl.DateTimeFormat(toIntlLocale(language), {
    day: 'numeric',
    month: 'long',
    timeZone: options.timeZone,
  }).format(date);
  return { day, time };
}

/** Момент записи журнала строкой («14 марта · 12:00»); некорректный момент — null. */
export function formatEventMoment(
  value: string | Date,
  language: string,
  options: MomentOptions = {},
): string | null {
  const moment = eventMoment(value, language, options);
  return moment ? joinDayTime(moment) : null;
}
